import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import {
  createAdminDealer,
  fetchAdminDealer,
  trackDealerEvent,
  updateAdminDealer,
} from "../../../services/automapDealerOnboardingApi";
import { AdminDealerNav } from "./dealerAdminShared";

const EMPTY = {
  businessName: "",
  legalName: "",
  taxId: "",
  city: "",
  neighborhood: "",
  address: "",
  mainWhatsapp: "",
  mainEmail: "",
  websiteUrl: "",
  instagramUrl: "",
  logoUrl: "",
  description: "",
  offersFinancing: "",
  estimatedInventoryCount: "",
  assignedSalesOwnerId: "",
  assignedSalesOwner: "",
  slug: "",
  status: "",
  verificationStatus: "",
  planType: "founding_free",
  coverImageUrl: "",
  internalNotes: "",
  publicProfileEnabled: false,
  responseTimeMinutes: "",
};

function AdminDealerFormPageContent() {
  const { dealerId } = useParams();
  const isEdit = Boolean(dealerId);
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchAdminDealer(dealerId);
        const dealer = data.dealer;
        if (cancelled || !dealer) return;
        setForm({
          businessName: dealer.businessName || "",
          legalName: dealer.legalName || "",
          taxId: dealer.taxId || "",
          city: dealer.city || "",
          neighborhood: dealer.neighborhood || "",
          address: dealer.address || "",
          mainWhatsapp: dealer.mainWhatsapp || "",
          mainEmail: dealer.mainEmail || "",
          websiteUrl: dealer.websiteUrl || "",
          instagramUrl: dealer.instagramUrl || "",
          logoUrl: dealer.logoUrl || "",
          description: dealer.description || "",
          offersFinancing: dealer.offersFinancing || "",
          estimatedInventoryCount: dealer.estimatedInventoryCount ?? "",
          assignedSalesOwnerId: dealer.assignedSalesOwnerId || "",
          assignedSalesOwner: dealer.assignedSalesOwner || "",
          slug: dealer.slug || "",
          status: dealer.status || "",
          verificationStatus: dealer.verificationStatus || "",
          planType: dealer.planType || "founding_free",
          coverImageUrl: dealer.coverImageUrl || "",
          internalNotes: dealer.internalNotes || "",
          publicProfileEnabled: Boolean(dealer.publicProfileEnabled),
          responseTimeMinutes: dealer.responseTimeMinutes ?? "",
        });
      } catch (err) {
        setErrorMessage(err.message || "No se pudo cargar el concesionario.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealerId, isEdit]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const buildPayload = (status, startTrial = false) => ({
    ...form,
    status,
    estimatedInventoryCount: form.estimatedInventoryCount
      ? Number(form.estimatedInventoryCount)
      : null,
    responseTimeMinutes: form.responseTimeMinutes ? Number(form.responseTimeMinutes) : null,
    publicProfileEnabled: Boolean(form.publicProfileEnabled),
    startTrial,
  });

  const save = async (status, startTrial = false) => {
    if (!canManage) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const payload = buildPayload(status, startTrial);
      if (isEdit) {
        const { status: _status, startTrial: _startTrial, ...patch } = buildPayload(undefined, false);
        await updateAdminDealer(dealerId, patch);
        trackDealerEvent("dealer_updated", { dealerId });
        navigate(`/admin/dealers/${dealerId}`);
      } else {
        const data = await createAdminDealer(payload);
        trackDealerEvent("dealer_created", { dealerId: data.dealer?.id });
        navigate(`/admin/dealers/${data.dealer.id}`);
      }
    } catch (err) {
      setErrorMessage(err.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout>
        <AdminStatePanel tone="info" title="Cargando" message="Cargando concesionario…" />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Concesionarios"
        title={isEdit ? "Editar concesionario" : "Nuevo concesionario"}
        navigation={<AdminDealerNav active={isEdit ? "list" : "new"} />}
      />
      <main className="admin-console__main" style={{ maxWidth: 820 }}>
        {errorMessage ? <AdminStatePanel tone="danger" title="Error" message={errorMessage} /> : null}
        <form className="admin-form-grid" onSubmit={(e) => e.preventDefault()}>
          {[
            ["businessName", "Nombre comercial *"],
            ["legalName", "Razón social"],
            ["taxId", "NIT (opcional)"],
            ["city", "Ciudad"],
            ["neighborhood", "Barrio"],
            ["address", "Dirección"],
            ["mainWhatsapp", "WhatsApp"],
            ["mainEmail", "Email"],
            ["websiteUrl", "Sitio web"],
            ["instagramUrl", "Instagram"],
            ["logoUrl", "Logo URL"],
            ["assignedSalesOwner", "Sales owner"],
            ["slug", "Slug (URL)"],
            ["estimatedInventoryCount", "Inventario estimado"],
          ].map(([field, label]) => (
            <label key={field}>
              {label}
              <input value={form[field]} onChange={(e) => update(field, e.target.value)} />
            </label>
          ))}
          {isEdit ? (
            <>
              <label>Estado
                <select value={form.status} onChange={(e) => update("status", e.target.value)}>
                  {["prospect", "onboarding", "active", "paused", "suspended", "rejected", "archived"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>Verificación
                <select value={form.verificationStatus} onChange={(e) => update("verificationStatus", e.target.value)}>
                  {["unverified", "pending", "verified", "rejected", "suspended"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>Plan
                <select value={form.planType} onChange={(e) => update("planType", e.target.value)}>
                  {["founding_free", "free", "starter", "growth", "premium"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          <label className="admin-form-full">
            Cover URL
            <input value={form.coverImageUrl} onChange={(e) => update("coverImageUrl", e.target.value)} />
          </label>
          <label className="admin-form-full">
            ¿Ofrece financiación?
            <select value={form.offersFinancing} onChange={(e) => update("offersFinancing", e.target.value)}>
              <option value="">—</option>
              <option value="yes">Sí</option>
              <option value="no">No</option>
              <option value="sometimes">A veces</option>
            </select>
          </label>
          <label className="admin-form-full">
            Descripción
            <textarea value={form.description} onChange={(e) => update("description", e.target.value)} />
          </label>
          <label className="admin-form-full">
            Notas internas
            <textarea value={form.internalNotes} onChange={(e) => update("internalNotes", e.target.value)} />
          </label>
          <label>
            Tiempo de respuesta (min)
            <input type="number" min="0" value={form.responseTimeMinutes} onChange={(e) => update("responseTimeMinutes", e.target.value)} />
          </label>
          <label className="admin-form-full">
            <input type="checkbox" checked={form.publicProfileEnabled} onChange={(e) => update("publicProfileEnabled", e.target.checked)} />
            {" "}Perfil público habilitado
          </label>
        </form>
        {canManage ? (
          <div className="admin-console__header-actions" style={{ marginTop: "1rem" }}>
            {!isEdit ? (
              <>
                <button type="button" className="admin-btn" disabled={saving} onClick={() => save("prospect")}>Guardar como prospecto</button>
                <button type="button" className="admin-btn" disabled={saving} onClick={() => save("onboarding")}>Guardar como onboarding</button>
                <button type="button" className="admin-btn admin-btn--primary" disabled={saving} onClick={() => save("onboarding", true)}>Guardar e iniciar trial</button>
              </>
            ) : (
              <button type="button" className="admin-btn admin-btn--primary" disabled={saving} onClick={() => save(undefined)}>Guardar cambios</button>
            )}
            <Link className="admin-btn admin-btn--ghost" to={isEdit ? `/admin/dealers/${dealerId}` : "/admin/dealers"}>Cancelar</Link>
          </div>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminDealerFormPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminDealerFormPageContent />
    </RequirePermission>
  );
}
